import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingMedium,
    HeadingSmall,
    HeadingXSmall,
} from "@khanacademy/wonder-blocks-typography";
import {staticUrl} from "@repo/urls";

import {requireYouTubeAPI} from "#video-package/util.ts";

import type {CanAccessStates} from "./check-access.tsx";
import FeedbackMessage from "./feedback-message.tsx";

type Props = Record<any, any>;

type State = {
    canAccessYouTube: CanAccessStates;
    videoQuizStep: number;
    recommendations: Array<number>;
};

const VideoQuizSteps = {
    UNSTARTED: 0,
    CHECK_YOUTUBE: 1,
    YOUTUBE_VIDEO: 2,
    HOSTED_VIDEO: 3,
    RECOMMENDATIONS: 4,
} as const;

const Recommendations = {
    ALLOW_YOUTUBE: 1,
    ALLOW_NOCOOKIE: 2,
    ALLOW_IFRAME: 3,
    ALLOW_GOOGLE_VIDEO: 4,
    ALLOW_KASTATIC: 5,
} as const;
class VideoPlayerQuiz extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            canAccessYouTube: "LOADING",
            videoQuizStep: 0,
            recommendations: [],
        };
    }

    componentDidMount() {
        this._testAccessYouTube();
    }

    _testAccessYouTube: () => void = () => {
        const onSuccess = () => {
            this.setState({canAccessYouTube: "TRUE"});
        };
        const onFailure = () => {
            this.setState({canAccessYouTube: "FALSE"});
        };
        requireYouTubeAPI(onSuccess, onFailure);
    };

    _addRecommendation: (recommendation: number) => void = (
        recommendation: number,
    ) => {
        const recommendations = this.state.recommendations;
        recommendations.push(recommendation);
        this.setState({recommendations});
    };

    _renderVideoQuizButton: () => React.ReactNode = () => {
        return (
            <View>
                <Body>
                    {t`Get recommendations on how to fix or improve your video player experience.`}
                </Body>
                {/* native button because wonderblocks button will not work if kastatic is blocked */}
                <button
                    className={css(styles.videoQuizButton)}
                    onClick={() =>
                        this.setState({
                            videoQuizStep: VideoQuizSteps.CHECK_YOUTUBE,
                        })
                    }
                >
                    <Body>{t`Test Video Player Access`}</Body>
                </button>
            </View>
        );
    };
    _renderCheckYouTube: () => React.ReactNode = () => {
        let allowContinue = false;
        const {canAccessYouTube} = this.state;

        if (
            this.state.canAccessYouTube === "TRUE" ||
            this.state.canAccessYouTube === "FALSE"
        ) {
            allowContinue = true;
        }
        return (
            <View>
                <HeadingSmall style={styles.heading}>
                    {t`YouTube Access`}
                </HeadingSmall>
                <Body>
                    {canAccessYouTube === "LOADING" && (
                        <CircularSpinner size="small" />
                    )}
                    {canAccessYouTube === "TRUE" && (
                        <FeedbackMessage
                            message={t`Youtube.com check passed`}
                            success={true}
                        />
                    )}
                    {canAccessYouTube === "FALSE" && (
                        <FeedbackMessage
                            message={t`Youtube.com check failed`}
                            success={false}
                        />
                    )}
                </Body>
                {/* native button because wonderblocks button will not work if kastatic is blocked */}
                <button
                    className={css(styles.videoQuizButton)}
                    disabled={!allowContinue}
                    onClick={() => {
                        if (canAccessYouTube) {
                            this.setState({
                                videoQuizStep: VideoQuizSteps.YOUTUBE_VIDEO,
                            });
                        } else {
                            this._addRecommendation(
                                Recommendations.ALLOW_YOUTUBE,
                            );
                            this.setState({
                                videoQuizStep: VideoQuizSteps.HOSTED_VIDEO,
                            });
                        }
                    }}
                >
                    <Body>{t`Continue`}</Body>
                </button>
            </View>
        );
    };

    _renderYouTubeVideo: () => React.ReactNode = () => {
        return (
            <View>
                <HeadingSmall style={styles.heading}>
                    {t`YouTube Video Player`}
                </HeadingSmall>
                <HeadingXSmall style={styles.heading}>
                    {t`Can you play this video? (1/2)`}
                </HeadingXSmall>
                {/* eslint-disable-next-line jsx-a11y/iframe-has-title -- TODO: Address a11y error */}
                <iframe
                    src="https://www.youtube-nocookie.com/embed/uIojjqSm0m4/"
                    height={300}
                    width={400}
                />
                <View style={styles.yesNoContainer}>
                    {/* native buttons because wonderblocks button will not work if kastatic is blocked */}
                    <button
                        className={css(styles.videoQuizButton)}
                        onClick={() =>
                            this.setState({
                                videoQuizStep: VideoQuizSteps.HOSTED_VIDEO,
                            })
                        }
                    >
                        <Body>{t`Yes`}</Body>
                    </button>
                    <button
                        className={css(
                            styles.videoQuizButton,
                            styles.videoQuizDestructiveButton,
                        )}
                        onClick={() => {
                            this._addRecommendation(
                                Recommendations.ALLOW_IFRAME,
                            );
                            this._addRecommendation(
                                Recommendations.ALLOW_NOCOOKIE,
                            );
                            this._addRecommendation(
                                Recommendations.ALLOW_GOOGLE_VIDEO,
                            );
                            this.setState({
                                videoQuizStep: VideoQuizSteps.HOSTED_VIDEO,
                            });
                        }}
                    >
                        <Body>{t`No`}</Body>
                    </button>
                </View>
            </View>
        );
    };

    _renderHostedVideo: () => React.ReactNode = () => {
        return (
            <View>
                <HeadingSmall style={styles.heading}>
                    {t`Backup Video Player`}
                </HeadingSmall>
                <HeadingXSmall style={styles.heading}>
                    {t`Can you play this video? (2/2)`}
                </HeadingXSmall>
                {/* eslint-disable jsx-a11y/media-has-caption -- TODO: Address a11y error */}
                {/* @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'number | undefined'. */}
                <video controls tabIndex="0" height={300} width={400}>
                    {/* eslint-enable jsx-a11y/media-has-caption -- TODO: Address a11y error */}
                    <source
                        src={staticUrl(
                            "http://storage.googleapis.com/ka-youtube-converted/uIojjqSm0m4.mp4/uIojjqSm0m4.mp4",
                        )}
                        type="video/mp4"
                    />
                </video>
                <View style={styles.yesNoContainer}>
                    {/* native buttons because wonderblocks button will not work if kastatic is blocked */}
                    <button
                        className={css(styles.videoQuizButton)}
                        onClick={() => {
                            this.setState({
                                videoQuizStep: VideoQuizSteps.RECOMMENDATIONS,
                            });
                        }}
                    >
                        <Body>{t`Yes`}</Body>
                    </button>
                    <button
                        className={css(
                            styles.videoQuizButton,
                            styles.videoQuizDestructiveButton,
                        )}
                        onClick={() => {
                            this._addRecommendation(
                                Recommendations.ALLOW_KASTATIC,
                            );
                            this.setState({
                                videoQuizStep: VideoQuizSteps.RECOMMENDATIONS,
                            });
                        }}
                    >
                        <Body>{t`No`}</Body>
                    </button>
                </View>
            </View>
        );
    };

    _renderVideoRecommendations: () => React.ReactNode = () => {
        const recommendations = this.state.recommendations;
        const helpfulMessages = {
            [Recommendations.ALLOW_YOUTUBE]: t`Ensure youtube.com is not blocked`,
            [Recommendations.ALLOW_NOCOOKIE]: t`Ensure youtube-nocookie.com is not blocked`,
            [Recommendations.ALLOW_GOOGLE_VIDEO]: t`Ensure googlevideo.com is not blocked`,
            [Recommendations.ALLOW_KASTATIC]: t`Ensure kastatic.org is not blocked`,
            [Recommendations.ALLOW_IFRAME]: t`Ensure that showing an iframe from another origin is not blocked`,
        } as const;

        const youTubeBlocked = !this.state.canAccessYouTube;

        const fallbackBlocked =
            recommendations.indexOf(Recommendations.ALLOW_KASTATIC) !== -1;

        const youTubePlayerBlocked =
            recommendations.filter((r) => r !== Recommendations.ALLOW_KASTATIC)
                .length > 0;

        return (
            <View>
                {youTubeBlocked ? (
                    <FeedbackMessage
                        message={t`Cannot access youtube.com`}
                        success={false}
                    />
                ) : (
                    <FeedbackMessage
                        message={t`Can access youtube.com`}
                        success={true}
                    />
                )}
                {youTubePlayerBlocked ? (
                    <FeedbackMessage
                        message={t`Cannot use the YouTube player`}
                        success={false}
                    />
                ) : (
                    <FeedbackMessage
                        message={t`Can use the YouTube player`}
                        success={true}
                    />
                )}
                {fallbackBlocked ? (
                    <FeedbackMessage
                        message={t`Cannot use the backup player`}
                        success={false}
                    />
                ) : (
                    <FeedbackMessage
                        message={t`Can use the backup player`}
                        success={true}
                    />
                )}
                <Strut size={spacing.medium_16} />
                <Body>
                    {!fallbackBlocked && youTubePlayerBlocked && (
                        <React.Fragment>
                            {t`To access additional video playback controls, follow these guidelines to unblock the YouTube player.`}
                            <Strut size={spacing.small_12} />
                            <Link href="https://support.khanacademy.org/hc/en-us/articles/204794840-What-features-does-the-video-player-have-">
                                {t`Learn more`}
                            </Link>
                        </React.Fragment>
                    )}
                </Body>
                {recommendations.length > 0 && (
                    <React.Fragment>
                        <HeadingSmall style={styles.heading}>
                            {t`Recommendations`}
                        </HeadingSmall>
                        <ul>
                            {recommendations.map((recommendation, index) => (
                                <li key={index}>
                                    <Body>
                                        {helpfulMessages[recommendation]}
                                    </Body>
                                </li>
                            ))}
                        </ul>
                    </React.Fragment>
                )}
            </View>
        );
    };

    render(): React.ReactNode {
        let quizContent;

        switch (this.state.videoQuizStep) {
            case VideoQuizSteps.UNSTARTED:
                quizContent = this._renderVideoQuizButton();
                break;
            case VideoQuizSteps.CHECK_YOUTUBE:
                quizContent = this._renderCheckYouTube();
                break;
            case VideoQuizSteps.YOUTUBE_VIDEO:
                quizContent = this._renderYouTubeVideo();
                break;
            case VideoQuizSteps.HOSTED_VIDEO:
                quizContent = this._renderHostedVideo();
                break;
            case VideoQuizSteps.RECOMMENDATIONS:
                quizContent = this._renderVideoRecommendations();
                break;
            default:
                quizContent = this._renderVideoQuizButton();
        }

        return (
            <View>
                <HeadingMedium style={styles.heading}>
                    {t`Playing Videos on Khan Academy`}
                </HeadingMedium>
                {quizContent}
            </View>
        );
    }
}

const styles = StyleSheet.create({
    heading: {
        margin: "1em 0",
        flexDirection: "row",
        alignItems: "baseline",
    },
    videoQuizButton: {
        // should look similar to a wonderblocks button
        backgroundColor: color.blue,
        margin: ".5em 0",
        color: color.white,
        padding: spacing.small_12,
        border: "none",
        borderRadius: 4,
        cursor: "pointer",
    },
    videoQuizDestructiveButton: {
        backgroundColor: color.red,
    },
    yesNoContainer: {
        display: "grid",
        gridTemplateColumns: "auto auto",
        gridGap: 12,
        marginTop: 12,
        maxWidth: 400,
    },
});

export default VideoPlayerQuiz;
